import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "row gx-3" }
const _hoisted_2 = { class: "col-12 audio-player-col ps-2" }
const _hoisted_3 = {
  class: "bg-secondary text-center bg-opacity-05 px-3 pt-5",
  style: {"min-height":"460px"}
}
const _hoisted_4 = {
  style: {"width":"110px"},
  class: "mt-3 m-auto"
}
const _hoisted_5 = ["src"]
const _hoisted_6 = ["src"]
const _hoisted_7 = {
  key: 2,
  class: "w-100 d-flex align-items-center justify-content-center display-3 text-danger",
  style: {"aspect-ratio":"1 / 1.4"}
}
const _hoisted_8 = { class: "mt-5" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { class: "mt-5 d-flex align-items-center gap-3" }
const _hoisted_12 = { class: "text-muted h6 mb-0 progress-time progress-time--left" }
const _hoisted_13 = { class: "text-muted h6 mb-0 progress-time progress-time--right" }
const _hoisted_14 = { class: "position-relative d-flex justify-content-center align-items-center mt-4 text-white mb-3" }
const _hoisted_15 = ["title"]
const _hoisted_16 = ["title"]
const _hoisted_17 = {
  class: "col-12 audio-playlist-col p-2",
  style: {"display":"flex","flex-direction":"column","gap":"1rem"}
}

import { computed, ref } from "vue";
import { useEventListener } from "@vueuse/core";
import { useAudio } from "@/store/modules/media/audio-list";
import { useAudioControls } from "@/composables/audio/useAudioControls";
import { useShuffle } from "@/composables/useShuffle";
import { useVRoom } from "@/store/modules/hotel/virtual-room";

import type { UseAudioList as Audio } from "@/@types/audio/use_audio";

import FormatTime from "../app/_shared/format/format-time.vue";
import IconComponent from "../app/_shared/icons/icon-component.vue";
import ListHead from "@/views/audio-next/ListHead.vue";
import ListItem from "@/components/common-ui/lists/ListItem.vue";
import ListItemAudio from "@/views/audio-next/ListItemAudio.vue";
import CustomScrollVertical from "@/views/audio-next/CustomScrollVertical.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'standart-audio-player-list',
  setup(__props) {

const audioStore = useAudio();
const progressBarRef = ref<HTMLDivElement | null>(null);
const {
  isPlaying,
  togglePlay,
  currentTrack,
  next,
  previous,
  playlist,
  play,
  setCurrentTime,
} = useAudioControls();

// Получаем shuffled-список и метод shuffle из composable
const { shuffled, shuffle: shuffleTrackList } = useShuffle(playlist);

// ------------ Продолжить (история vroom) ------------
const vroom = useVRoom();
const continueEntry = computed(() => {
  const bookId = currentTrack.value?.path || "";
  if (!bookId) return null;
  const entry = vroom.getVroomAudiobooks.find((b) => b.book_id === bookId);
  if (!entry) return null;
  const time = Number(entry.current_time);
  if (!time || time <= 0) return null;
  return entry;
});

const continuePlayback = () => {
  const entry = continueEntry.value;
  if (!entry) return;

  const time = Number(entry.current_time) || 0;
  const tracks = audioStore.getTrackList;
  const chapter =
    tracks.find((t) => t._id === entry.filename) ||
    tracks[tracks.length - 1] ||
    currentTrack.value;
  if (!chapter) return;

  if (currentTrack.value?._id === chapter._id) {
    if (time > 0) setCurrentTime(time);
    if (!isPlaying.value) togglePlay();
    return;
  }

  const playlistWithTime = tracks.map((t) =>
    t._id === chapter._id ? { ...t, currentTime: time } : t
  );
  play({ track: chapter, playlist: playlistWithTime });
};

// ------------ computed совойства ------------
const currentTime = computed(() => audioStore.currentTime);
const duration = computed(() => audioStore.duration);
const progress = computed(() => audioStore.progress);

// ==================== Перетаскивание бегунка ====================
const isDragging = ref(false);
const seekValue = ref<number | null>(null);

// Локальный процент во время драга, иначе — из стора
const displayProgress = computed(() => {
  if (isDragging.value && seekValue.value !== null) {
    return seekValue.value;
  }
  return progress.value;
});

const calculateProgress = (clientX: number): number => {
  if (!progressBarRef.value || !duration.value) return 0;

  const rect = progressBarRef.value.getBoundingClientRect();
  const pos = (clientX - rect.left) / rect.width; // 0..1
  return Math.max(0, Math.min(100, pos * 100));
};

const startDrag = (event: PointerEvent) => {
  if (!currentTrack.value || !duration.value) return;

  isDragging.value = true;
  seekValue.value = calculateProgress(event.clientX);

  // Предотвращаем выделение текста и стандартные тач-действия при старте драга
  event.preventDefault();
};

// Движение указателя отслеживаем на window — работает и за пределами бара
useEventListener(window, "pointermove", (event: PointerEvent) => {
  if (!isDragging.value) return;
  seekValue.value = calculateProgress(event.clientX);
});

// Отпускание указателя — применяем итоговую позицию
useEventListener(window, "pointerup", (event: PointerEvent) => {
  if (!isDragging.value) return;

  const finalPercent = calculateProgress(event.clientX);
  const seekTime = (finalPercent / 100) * duration.value;
  setCurrentTime(seekTime);

  isDragging.value = false;
  seekValue.value = null;
});

const selectTrack = (track: Audio) => {
  if (!track || !shuffled.value) return;
  play({ track, playlist: [...shuffled.value] });
};

const handlePlayPauseTrack = (track: Audio) => {
  if (currentTrack.value?._id === track._id) {
    togglePlay();
  } else {
    selectTrack(track);
  }
};

// Обработчик шафлинга треков с обновлением очереди (по аналогии с референсом)
const handleShuffleTrackList = () => {
  // 1. Шафлим локальный список для отображения
  shuffleTrackList();

  // 2. Если что-то играет и текущий трек есть в новом шафл-списке — обновляем очередь
  if (isPlaying.value && currentTrack.value) {
    const currentTrackInShuffled = shuffled.value.find(
      (t) => t._id === currentTrack.value?._id
    );

    if (currentTrackInShuffled) {
      play({ track: currentTrackInShuffled, playlist: [...shuffled.value] });
    }
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          (_unref(currentTrack)?.coverUrl)
            ? (_openBlock(), _createElementBlock("img", {
                key: 0,
                src: _unref(currentTrack).coverUrl,
                alt: "",
                class: "w-100"
              }, null, 8, _hoisted_5))
            : (_unref(currentTrack)?.src?.poster && _unref(currentTrack)?.src?.folder)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 1,
                  src: _ctx.$getWebImage(_unref(currentTrack).src.poster, _unref(currentTrack).src.folder),
                  alt: "",
                  class: "w-100"
                }, null, 8, _hoisted_6))
              : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _createVNode(IconComponent, {
                    lib: "filoxenia",
                    icon: "MusicNote"
                  })
                ]))
        ]),
        _createElementVNode("p", _hoisted_8, [
          (_unref(currentTrack)?.name)
            ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(_unref(currentTrack).name), 1))
            : (_openBlock(), _createElementBlock("span", _hoisted_10, "ㅤ"))
        ]),
        _createElementVNode("div", _hoisted_11, [
          _createElementVNode("span", _hoisted_12, [
            _createVNode(FormatTime, { seconds: currentTime.value }, null, 8, ["seconds"])
          ]),
          _createElementVNode("div", {
            class: "progress-bar flex-grow-1",
            ref_key: "progressBarRef",
            ref: progressBarRef,
            onPointerdown: startDrag
          }, [
            _createElementVNode("div", {
              class: "progress h-100",
              style: _normalizeStyle({ width: displayProgress.value + '%' })
            }, null, 4),
            _createElementVNode("div", {
              class: "progress-thumb",
              style: _normalizeStyle({ left: displayProgress.value + '%' })
            }, null, 4)
          ], 544),
          _createElementVNode("span", _hoisted_13, [
            _createVNode(FormatTime, { seconds: duration.value }, null, 8, ["seconds"])
          ])
        ]),
        _createElementVNode("div", _hoisted_14, [
          _createElementVNode("button", {
            class: "control-btns",
            onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(previous) && _unref(previous)(...args)))
          }, [...(_cache[3] || (_cache[3] = [
            _createElementVNode("i", { class: "bi bi-skip-backward-fill" }, null, -1)
          ]))]),
          _createElementVNode("button", {
            class: "control-btns play-btns",
            onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(togglePlay) && _unref(togglePlay)(...args))),
            title: _unref(isPlaying) ? _ctx.$t('media.pause') : _ctx.$t('media.play')
          }, [
            _createElementVNode("i", {
              class: _normalizeClass(_unref(isPlaying) ? 'bi-pause-fill' : 'bi-play-fill')
            }, null, 2)
          ], 8, _hoisted_15),
          _createElementVNode("button", {
            class: "control-btns",
            onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(next) && _unref(next)(...args)))
          }, [...(_cache[4] || (_cache[4] = [
            _createElementVNode("i", { class: "bi bi-skip-forward-fill" }, null, -1)
          ]))]),
          (continueEntry.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "control-btns continue-btns position-absolute end-0",
                onClick: continuePlayback,
                title: _ctx.$t('buttons.continue')
              }, [
                _cache[5] || (_cache[5] = _createElementVNode("i", { class: "bi bi-play-circle-fill me-2" }, null, -1)),
                _createTextVNode(" " + _toDisplayString(_ctx.$t("buttons.continue")), 1)
              ], 8, _hoisted_16))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_17, [
      _createVNode(ListHead, { onShuffle: handleShuffleTrackList }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t("audio.tracks")), 1)
        ]),
        _: 1
      }),
      _createVNode(CustomScrollVertical, null, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(shuffled), (t) => {
            return (_openBlock(), _createBlock(ListItem, {
              key: t._id,
              "is-active": _unref(currentTrack)?._id === t._id,
              onClick: ($event: any) => (selectTrack(t)),
              noPadding: ""
            }, {
              default: _withCtx(() => [
                _createVNode(ListItemAudio, {
                  title: t.name,
                  artist: t.artist,
                  "cover-url": t.coverUrl,
                  selected: _unref(currentTrack)?._id === t._id,
                  playing: _unref(currentTrack)?._id === t._id && _unref(isPlaying),
                  "onUpdate:playing": () => handlePlayPauseTrack(t)
                }, null, 8, ["title", "artist", "cover-url", "selected", "playing", "onUpdate:playing"])
              ]),
              _: 2
            }, 1032, ["is-active", "onClick"]))
          }), 128))
        ]),
        _: 1
      })
    ])
  ]))
}
}

})